import React, { useEffect, useRef, useState, useContext } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import {
  Grid, LinearProgress, Typography, Button, Divider,
  Card, CardHeader, CardContent, Box,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  IconButton, Chip, Avatar, Tooltip,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SendIcon from "@material-ui/icons/Send";
import PhoneIcon from "@material-ui/icons/Phone";
import MessageIcon from "@material-ui/icons/Message";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ListAltIcon from "@material-ui/icons/ListAlt";
import GetAppIcon from "@material-ui/icons/GetApp";
import { useDate } from "../../hooks/useDate";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { has, get, isNull } from "lodash";
import api from "../../services/api";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title as ChartTitle, Tooltip as ChartTooltip, Legend, Filler,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { i18n } from "../../translate/i18n";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, ChartTitle, ChartTooltip, Legend, Filler);

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1, padding: theme.spacing(2),
    overflowY: "scroll", ...theme.scrollbarStyles, marginBottom: theme.spacing(2),
  },
  statCard: {
    padding: theme.spacing(2), borderRadius: 10,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    display: "flex", alignItems: "center", gap: theme.spacing(1.5),
  },
  statIcon: {
    width: 44, height: 44, borderRadius: 10,
    background: "rgba(91,45,142,0.1)", color: "#5B2D8E",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  statBody: {},
  statValue: { fontWeight: 700, fontSize: "1.5rem", lineHeight: 1.2, color: theme.palette.text.primary },
  statLabel: { fontSize: "0.78rem", color: theme.palette.text.secondary },
  mobileStats: {
    display: "grid", gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1.5), padding: theme.spacing(1),
  },
  mobileHeader: {
    display: "flex", flexDirection: "column",
    gap: theme.spacing(1), width: "100%", padding: theme.spacing(0, 0, 1),
  },
  mobileHeaderRow: { display: "flex", alignItems: "center", gap: 8 },
  statusChip: { color: "#fff", fontWeight: "bold" },
  deliveredChip: { backgroundColor: theme.palette.success.main },
  pendingChip: { backgroundColor: theme.palette.warning.main },
  failedChip: { backgroundColor: theme.palette.error.main },
  avatar: { backgroundColor: theme.palette.primary.main, color: "#fff" },
  pieContainer: { height: 220, position: "relative", marginTop: theme.spacing(1) },
}));

const CampaignReport = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const { campaignId } = useParams();

  const [campaign, setCampaign] = useState({});
  const [delivered, setDelivered] = useState(0);
  const [validContacts, setValidContacts] = useState(0);
  const [uniqueNumbers, setUniqueNumbers] = useState(0);
  const [failedMessages, setFailedMessages] = useState(0);
  const [totalMessagesSent, setTotalMessagesSent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [messageRows, setMessageRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalShippingPages, setTotalShippingPages] = useState(1);
  const mounted = useRef(true);
  const { user, socket } = useContext(AuthContext);
  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  const [chartData, setChartData] = useState({
    labels: ["Entregues", "Aguardando", "Falhas"],
    datasets: [{ data: [0, 0, 0], backgroundColor: ["rgba(75,192,192,0.7)", "rgba(255,206,86,0.7)", "rgba(255,99,132,0.7)"], borderColor: ["rgba(75,192,192,1)", "rgba(255,206,86,1)", "rgba(255,99,132,1)"], borderWidth: 1 }],
  });

  const chartOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "bottom" } } };

  useEffect(() => {
    async function fetchData() {
      try {
        const planConfigs = await getPlanCompany(undefined, user.companyId);
        if (!planConfigs?.plan?.useCampaigns) {
          toast.error("Sem permissão."); setTimeout(() => history.push("/"), 1000);
        }
      } catch (err) {}
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (mounted.current) findCampaign();
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const companyId = user.companyId;
    const onEvent = (data) => {
      if (data.record.id === +campaignId) {
        setCampaign(data.record);
        if (data.record.status === "FINALIZADA") setTimeout(() => findCampaign(), 5000);
      }
    };
    socket.on(`company-${companyId}-campaign`, onEvent);
    return () => socket.off(`company-${companyId}-campaign`, onEvent);
  }, [campaignId]);

  const findCampaign = async () => {
    try {
      setLoading(true);
      const { data: camp } = await api.get(`/campaigns/${campaignId}`);
      setCampaign(camp);

      try {
        const { data: stats } = await api.get(`/campaigns/${campaignId}/stats`);
        setDelivered(stats.deliveredMessages);
        setValidContacts(stats.totalMessages);
        setUniqueNumbers(stats.uniqueNumbers);
        setFailedMessages(stats.failedMessages);
        setTotalMessagesSent(stats.totalMessages);
        setChartData({
          labels: ["Entregues", "Aguardando", "Falhas"],
          datasets: [{ data: [stats.deliveredMessages, stats.pendingMessages, stats.failedMessages], backgroundColor: ["rgba(75,192,192,0.7)", "rgba(255,206,86,0.7)", "rgba(255,99,132,0.7)"], borderColor: ["rgba(75,192,192,1)", "rgba(255,206,86,1)", "rgba(255,99,132,1)"], borderWidth: 1 }],
        });
      } catch (e) {}

      try {
        const { data: sr } = await api.get(`/campaigns/${campaignId}/shipping?page=1&pageSize=${rowsPerPage}`);
        if (sr?.shipping) {
          setMessageRows(sr.shipping.map(item => ({
            id: item.id, jobId: item.jobId, number: item.number,
            message: item.message ? item.message.substring(0, 50) + (item.message.length > 50 ? "..." : "") : "",
            fullMessage: item.message || "",
            deliveredAt: item.deliveredAt ? datetimeToClient(item.deliveredAt) : null,
            createdAt: item.createdAt ? datetimeToClient(item.createdAt) : null,
            status: item.deliveredAt ? "delivered" : "pending",
          })));
          setTotalShippingPages(sr.totalPages || 1);
        }
      } catch (e) {}
    } catch (error) {
      toast.error(i18n.t("campaignReport.fetchError"));
    } finally { setLoading(false); }
  };

  const loadMoreShipping = async (newPage = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const { data: sr } = await api.get(`/campaigns/${campaignId}/shipping?page=${newPage}&pageSize=${pageSize}`);
      if (sr?.shipping) {
        setMessageRows(sr.shipping.map(item => ({
          id: item.id, jobId: item.jobId, number: item.number,
          message: item.message ? item.message.substring(0, 50) + (item.message.length > 50 ? "..." : "") : "",
          fullMessage: item.message || "",
          deliveredAt: item.deliveredAt ? datetimeToClient(item.deliveredAt) : null,
          createdAt: item.createdAt ? datetimeToClient(item.createdAt) : null,
          status: item.deliveredAt ? "delivered" : "pending",
        })));
        setTotalShippingPages(sr.totalPages || 1);
      }
    } catch (e) { toast.error("Error al cargar datos"); setMessageRows([]); }
    finally { setLoading(false); }
  };

  const formatStatus = (val) => {
    const map = { INATIVA: "Inactiva", PROGRAMADA: "Programada", EM_ANDAMENTO: "En curso", CANCELADA: "Cancelada", FINALIZADA: "Finalizada" };
    return map[val] || val;
  };

  const formatPhone = (number) => {
    if (!number) return "";
    if (number.length > 15) return `Grupo (${number.substring(0, 6)}...)`;
    if (number.length === 12 && number.startsWith("55")) {
      const ddd = number.substring(2, 4);
      return `+55 (${ddd}) ${number.substring(4, 9)}-${number.substring(9)}`;
    }
    return number;
  };

  const getStatusChip = (status) => {
    if (status === "delivered") return <Chip label="Entregado" size="small" icon={<DoneAllIcon fontSize="small" />} className={`${classes.statusChip} ${classes.deliveredChip}`} />;
    if (status === "failed") return <Chip label="Falla" size="small" icon={<ErrorIcon fontSize="small" />} className={`${classes.statusChip} ${classes.failedChip}`} />;
    return <Chip label="Pendiente" size="small" icon={<ScheduleIcon fontSize="small" />} className={`${classes.statusChip} ${classes.pendingChip}`} />;
  };

  const exportToExcel = async () => {
    try {
      toast.info("Generando reporte Excel...");
      let allData = []; let currentPage = 1; let hasMore = true;
      while (hasMore) {
        const { data: sr } = await api.get(`/campaigns/${campaignId}/shipping?page=${currentPage}&pageSize=1000`);
        if (!sr?.shipping) break;
        allData = [...allData, ...sr.shipping];
        hasMore = currentPage < sr.totalPages;
        currentPage++;
      }
      if (allData.length === 0) { toast.error("Sin datos"); return; }
      const rows = [
        ["Reporte de Campaña", "", "", "", "", ""],
        [`Campaña: ${campaign.name}`, "", "", "", "", ""],
        [`Generado: ${new Date().toLocaleString("es-MX")}`, "", "", "", "", ""],
        ["", "", "", "", "", ""],
        ["ID", "Destinatario", "Mensaje", "Enviado", "Entregado", "Status"],
        ...allData.map(i => [i.jobId || "", i.number || "", i.message || "", i.createdAt ? new Date(i.createdAt).toLocaleString("es-MX") : "", i.deliveredAt ? new Date(i.deliveredAt).toLocaleString("es-MX") : "", i.deliveredAt ? "Entregado" : "Pendiente"]),
      ];
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [{ wch: 15 }, { wch: 20 }, { wch: 50 }, { wch: 20 }, { wch: 20 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, ws, "Reporte");
      XLSX.writeFile(wb, `reporte_${campaign.name?.replace(/[^a-zA-Z0-9]/g, "_")}_${new Date().toISOString().split("T")[0]}.xlsx`);
      toast.success(`${allData.length} registros exportados.`);
    } catch (e) { toast.error("Error al exportar"); }
  };

  const statCards = [
    { icon: <SendIcon fontSize="small" />, value: delivered, label: "Mensajes enviados" },
    { icon: <PhoneIcon fontSize="small" />, value: uniqueNumbers, label: "Destinatarios únicos" },
    { icon: <CheckCircleIcon fontSize="small" />, value: delivered, label: "Entregados" },
    { icon: <ErrorIcon fontSize="small" />, value: failedMessages, label: "Fallidos" },
  ];

  return (
    <MainContainer>
      <MainHeader>
        {isMobile ? (
          <div className={classes.mobileHeader}>
            <div className={classes.mobileHeaderRow}>
              <IconButton size="small" onClick={() => history.push("/campaigns")}><ArrowBackIcon /></IconButton>
              <Title style={{ flex: 1, fontSize: "1rem" }}>{campaign.name || i18n.t("campaignReport.title")}</Title>
              {campaign.status && (
                <span style={{ fontSize: "0.72rem", fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: "rgba(91,45,142,0.1)", color: "#5B2D8E" }}>
                  {formatStatus(campaign.status)}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", width: "100%", gap: 12 }}>
            <Button variant="outlined" color="primary" onClick={() => history.push("/campaigns")} startIcon={<ArrowBackIcon />}>
              {i18n.t("campaignReport.backButton")}
            </Button>
            <Title>{i18n.t("campaignReport.title")} {campaign.name}</Title>
          </div>
        )}
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Stats */}
        {isMobile ? (
          <div className={classes.mobileStats}>
            {statCards.map((s, i) => (
              <div key={i} className={classes.statCard}>
                <div className={classes.statIcon}>{s.icon}</div>
                <div className={classes.statBody}>
                  <div className={classes.statValue}>{s.value}</div>
                  <div className={classes.statLabel}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Grid container spacing={3} style={{ marginBottom: 16 }}>
            {statCards.map((s, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h4">{s.value}</Typography>
                    <Typography color="textSecondary">{s.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Progreso */}
        <Box style={{ padding: "12px 4px" }}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2" color="textSecondary">{i18n.t("campaignReport.deliver")}</Typography>
            <Typography variant="body2">{delivered} de {totalMessagesSent}</Typography>
          </Box>
          <LinearProgress variant="determinate" style={{ height: 8, borderRadius: 4 }} value={(delivered / (totalMessagesSent || 1)) * 100} />
        </Box>

        {/* Gráfico */}
        {!isMobile && (
          <Grid container spacing={3} style={{ marginTop: 8, marginBottom: 16 }}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>Estado de entrega</Typography>
                  <div className={classes.pieContainer}><Doughnut data={chartData} options={chartOptions} /></div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                {campaign.whatsapp?.name && (
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardHeader avatar={<Avatar className={classes.avatar}><WhatsAppIcon /></Avatar>} title="Conexión WhatsApp" subheader={campaign.whatsapp.name} />
                    </Card>
                  </Grid>
                )}
                {campaign.contactList?.name && (
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardHeader avatar={<Avatar className={classes.avatar}><ListAltIcon /></Avatar>} title={i18n.t("campaignReport.contactLists")} subheader={campaign.contactList.name} />
                    </Card>
                  </Grid>
                )}
                {campaign.scheduledAt && (
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardHeader avatar={<Avatar className={classes.avatar}><ScheduleIcon /></Avatar>} title={i18n.t("campaignReport.schedule")} subheader={datetimeToClient(campaign.scheduledAt)} />
                    </Card>
                  </Grid>
                )}
                {campaign.completedAt && (
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardHeader avatar={<Avatar className={classes.avatar}><EventAvailableIcon /></Avatar>} title={i18n.t("campaignReport.conclusion")} subheader={datetimeToClient(campaign.completedAt)} />
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}

        <Divider style={{ margin: "16px 0 8px" }} />

        {/* Tabla de mensajes */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            <MessageIcon style={{ verticalAlign: "middle", marginRight: 6 }} />
            Detalle de mensajes
          </Typography>
          <Button variant="contained" color="primary" size="small" startIcon={<GetAppIcon />} onClick={exportToExcel}
            style={{ background: "linear-gradient(45deg,#4CAF50,#45a049)", borderRadius: 20, textTransform: "none" }}>
            Excel
          </Button>
        </Box>

        <TableContainer style={{ overflowX: "auto" }}>
          <Table size="small" aria-label="mensajes">
            <TableHead>
              <TableRow>
                {!isMobile && <TableCell>ID</TableCell>}
                <TableCell>Destinatario</TableCell>
                {!isMobile && <TableCell>Mensaje</TableCell>}
                {!isMobile && <TableCell>Enviado</TableCell>}
                <TableCell>Entregado</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messageRows.map((row) => (
                <TableRow key={row.id}>
                  {!isMobile && <TableCell>{row.jobId}</TableCell>}
                  <TableCell>
                    <Tooltip title={row.number}><span>{formatPhone(row.number)}</span></Tooltip>
                  </TableCell>
                  {!isMobile && (
                    <TableCell><Tooltip title={row.fullMessage}><span>{row.message}</span></Tooltip></TableCell>
                  )}
                  {!isMobile && <TableCell>{row.createdAt}</TableCell>}
                  <TableCell>{row.deliveredAt || "—"}</TableCell>
                  <TableCell>{getStatusChip(row.status)}</TableCell>
                </TableRow>
              ))}
              {messageRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isMobile ? 3 : 6} align="center">
                    {loading ? "Cargando..." : "Sin mensajes"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalMessagesSent}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => { setPage(newPage); loadMoreShipping(newPage + 1, rowsPerPage); }}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); loadMoreShipping(1, parseInt(e.target.value, 10)); }}
            labelRowsPerPage={isMobile ? "" : "Filas:"}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </TableContainer>
      </Paper>
    </MainContainer>
  );
};

export default CampaignReport;
